import React, {ReactComponentElement, useEffect, useMemo, useRef, useState} from "react";
import {Box, Tooltip, Typography, Zoom} from "@mui/material";
import {isMobile} from "react-device-detect";
import {ReactComponent as CheckMarkIcon} from "../../../../assets/icons/checkmark_icon.svg";
import {IFlyerRuntimePage, IFlyerRuntimePageSpot, ParamTypesDto, PromosState} from "../../../../dtos/flyer";
import {
    addSelectedSpot,
    changeLoadingStatusForSelectedFrame, removeSelectedSpot
} from "../../../../store/actionsCreator/selectedSpotActionsCreator";
import {
    toggleHelpScreen,
    toggleSearch,
    toggleShareMenu,
    toggleToc, toggleFrameClickPopup
} from "../../../../store/actionsCreator/controlsActionsCreator";
import {changeCarouselActiveSlide} from "../../../../store/actionsCreator/carouselActionsCreator";
import {useDispatch, useSelector} from "react-redux";
import {IAppState, ISelectedSpot} from "../../../../store/models";
import {makeStyles} from '@mui/styles';
import {
    calculateCorrectPageFromShareLink,
    calculateTotalPagesForFlyer,
    findCurrentTranslateLabel, getConvertedPageNumberToSlideNumber, getIconStyle, openUrlInAnotherTab,
    resetMobileZoom,
    scrollToPageForFlyer
} from "../../../../com/globalsFunctions/globalFunctions";
import {CustomTooltipContent} from "./customTooltipContent";
import {LayoutForFrame} from "./layoutForFrame";
import {useParams} from "react-router-dom";

const useStyles = makeStyles(() => ({
    spot: {
        position: `absolute`,
        cursor: `pointer`,
        boxSizing: `border-box`,
        zIndex: 1,
        '&.promoAlreadyInBasket': {
            '&.isNotForMobile': {
                '&:hover': {
                    '& .MuiTypography-root': {
                        display: `block`,
                        // fontFamily: props.fontFamily,
                        textAlign: 'initial',
                    }
                }
            }
        },
    },

    staticSpotElem: {
        height: `100%`,
        width: `100%`,
        position: `relative`,
        display: `flex`,
        borderRadius: `5px`,
        '&.promoAlreadyInBasket': {
            outline: `2px solid #38B524`,
            backgroundColor: `#38B52433`,
            '&.isNotForMobile': {
                '&:hover:not(.blockHoverEffect)': {
                    backgroundColor: `#38B52433`,
                    boxShadow: `rgba(17, 17, 26, 0.1) 0px 8px 24px, rgba(17, 17, 26, 0.1) 0px 16px 56px, rgba(17, 17, 26, 0.1) 0px 24px 80px`,
                    // borderRadius: `3%`,
                    // position: `absolute`,
                    // top: `0`,
                    // left: `0`,
                    '& > .frameIconWrapper': {
                        transform: `scale(1.6)`
                    },
                }
            }
        },
        '&.isNotForMobile:not(.promoAlreadyInBasket)': {
            '&:hover:not(.blockHoverEffect)': {
                backgroundColor: `#D9D9D933`,
                outline: `2px solid #ffffff`,
                boxShadow: `rgba(17, 17, 26, 0.1) 0px 8px 24px, rgba(17, 17, 26, 0.1) 0px 16px 56px, rgba(17, 17, 26, 0.1) 0px 24px 80px`,
                // borderRadius: `3%`,
                // position: `absolute`,
                // top: `0`,
                // left: `0`,
                '& > .frameIconWrapper': {
                    transform: `scale(1.6)`
                },
                '& .MuiTypography-root': {
                    display: `block`
                }
            }
        },
        "& > .frameIconWrapper": {
            transform: "scale(0)",
            "&.showFrameIcon": {
                transform: "scale(1)",
            }
        }
    },
    checkmarkIconBlock: {
        height: `30px`,
        // minWidth: `30px`,
        backgroundColor: `#38B524`,
        position: `absolute`,
        top: `-2px`,
        right: `-2px`,
        display: `flex`,
        alignItems: `center`,
        justifyContent: `center`,
        borderRadius: `5px`,
    },
    spotHasSearchValue: {
        backgroundColor: `#18A0FB66`,
        outline: `2px solid #18A0FB`,
        boxShadow: `rgba(17, 17, 26, 0.1) 0px 8px 24px, rgba(17, 17, 26, 0.1) 0px 16px 56px, rgba(17, 17, 26, 0.1) 0px 24px 80px`,
        borderRadius: `3%`,
    },
    customTooltip: {
        '&.MuiTooltip-tooltip': {
            position: `relative`,
            backgroundColor: `white`,
            color: `#000000`,
            padding: `0`,
            lineHeight: `1`,
            border: `none`,
            maxWidth: `150px`,
            margin: `5px!important`,
            borderRadius: `none`
        }
    },
}))


export interface FrameCoordinates {
    spotHeight: number;
    spotWidth: number;
    top: number,
    left: number,
}

interface Props {
    page: IFlyerRuntimePage;
    frame: IFlyerRuntimePageSpot;
    frameIndex: number;
    frameCoordinates: FrameCoordinates;
    // isPlaying: boolean;
    contentForMobile?: any;
    sliderRef?: any;
    framesWrapperRef?: any;
}

export const PageFrame: React.FC<Props> = ({
                                               page,
                                               frame,
                                               frameIndex,
                                               frameCoordinates,
                                               // isPlaying,
                                               contentForMobile,
                                               sliderRef,
                                               framesWrapperRef
                                           }) => {
    const dispatch = useDispatch();
    const {flyersBranch, myListState} = useSelector((state: IAppState) => state);
    const isVerticalScrolling = flyersBranch.data?.pageLayoutPc === 2 || flyersBranch.data?.pageLayoutPc === 3;
    const isOnePageOrientation: boolean = flyersBranch.data?.pageLayoutPc === 3 || flyersBranch.data?.pageLayoutPc === 4;
    const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);
    const {pageNumber} = useParams<ParamTypesDto>();
    //get lowest number in URL pages range
    const pageStringToNumberIndex = useMemo(() => calculateCorrectPageFromShareLink(pageNumber), [pageNumber]);
    //convert page number to slide number
    const currentSlideNumber = useMemo(() => getConvertedPageNumberToSlideNumber(pageStringToNumberIndex, isMobile || isOnePageOrientation)
        , [pageStringToNumberIndex, isMobile, isOnePageOrientation]);
    const {searchValue} = useSelector(
        (state: IAppState) => state.searchSpotsOnPageReducer
    );
    const {spot, isExpanded} = useSelector<IAppState, ISelectedSpot>(state => state.selectedSpot);
    const {isHelpScreen, isTocOpen, showSearch, shareMenu} = useSelector(
        (state: IAppState) => state.controlsBranch
    );
    const [isFramePromoExistInMyList, setIsFramePromoExistInMyList] = useState<boolean>(false);
    const [isSmallFrameWidth, setIsSmallFrameWidth] = useState<boolean>(false);
    const [isFrameContainedSearchWord, setIsFrameContainedSearchWord] = useState<boolean>(false);
    const [allowedProps, setAllowedProps] = useState<any>({});
    const [icon, setIcon] = useState<ReactComponentElement<any>>(<></>);
    const [isShowFrame, setIsShowFrame] = useState<boolean>(false);
    const pickedMultiLangCaption = findCurrentTranslateLabel(656, 'Picked', flyersBranch.data);
    const isHebrewStyle = flyersBranch.data?.dir === "RTL";
    const iconStyle = flyersBranch.data?.iconContrast || 1;
    const mainColor = flyersBranch.data?.primaryColorRgb || "#C32E43";

    const classes = useStyles();

	const frameTooltip = frame.tooltip
		? frame.frameType === 2
			? `${frame.tooltip} ${frame.frameLink}`
			: frame.tooltip
		: frame.promo?.promoDisplayCaption1
			? frame.promo.promoDisplayCaption1
			: '';
    const isShowTooltip = !isMobile && frameTooltip;

	useEffect(() => {
        const isFramePromoExistInMyList = myListState.promos.some((promo: PromosState) => promo.promoId === frame.promo?.promoId);
        setIsFramePromoExistInMyList(isFramePromoExistInMyList);
    }, [myListState.promos.length]);

    useEffect(() => {
        const isSmallFrameWidth = frameCoordinates.spotWidth < 124;
        setIsSmallFrameWidth(isSmallFrameWidth);
    }, [frameCoordinates.spotWidth])

    useEffect(() => {
        // Get the promo caption from the frame, or set it to an empty string if it is undefined.
        const promoCaption = frame.promo?.promoDisplayCaption1 ?? '';

        // Check if search value is not empty and defined.
        // Check if promo caption contains the search value.
        // Set the state variable accordingly.
        setIsFrameContainedSearchWord(
            searchValue ? Boolean(searchValue) && promoCaption.toLowerCase().includes(searchValue.toLowerCase()) : false
        );
    }, [frame, searchValue]);

    useEffect(() => {
        const { frameType, spotId, promo, frameLink } = frame;
        const iconStyleData = getIconStyle(iconStyle);
        const IconPromoComp = iconStyleData.iconStyleData.promoCompIcon;
        const IconPageComp = iconStyleData.iconStyleData.pageCompIcon;
        const IconLinkComp = iconStyleData.iconStyleData.linkCompIcon;
        const IconVideoComp = iconStyleData.iconStyleData.videoCompIcon;
        const IconImageComp = iconStyleData.iconStyleData.imgCompIcon;
        const Icon360Comp = iconStyleData.iconStyleData.img360CompIcon;

        // Create an object with the allowed props for the component.
        const allowedProps: any = {
            "data-for-qa": `spot${frameIndex}`,
            "data-frameid": `${spotId}`,
            "data-frametype": `${frameType}`,
            "data-page-no": `${page.pageNumber}`,
        };

        // Define the icon component based on the frame type.
        let frameIcon;
        switch (frameType) {
            case 1:
                frameIcon = <IconPromoComp style={{width: '100%', height: '100%'}}/>;
                allowedProps["data-promo-id"] = promo?.promoId || "";
                allowedProps["data-promo-desc"] = promo?.promoDisplayCaption1 || "";
                break;
            case 2:
                frameIcon = <IconPageComp style={{width: '100%', height: '100%'}}/>;
                allowedProps["data-pagejump-no"] = frameLink;
                break;
            case 3:
                frameIcon = <IconLinkComp style={{width: '100%', height: '100%'}}/>;
                allowedProps["data-framelink"] = frameLink;
                break;
            case 4:
                frameIcon = <IconVideoComp style={{width: '100%', height: '100%'}}/>;
                allowedProps["data-framelink"] = frameLink;
                break;
            case 5:
                frameIcon = <IconImageComp style={{width: '100%', height: '100%'}}/>;
                allowedProps["data-framelink"] = frameLink;
                break;
            case 6:
                frameIcon = <Icon360Comp style={{width: '100%', height: '100%'}}/>;
                allowedProps["data-framelink"] = frameLink;
                break;
            default:
                frameIcon = <></>;
                break;
        }

        // Set the allowed props and icon component state variables.
        setAllowedProps(allowedProps);
        setIcon(frameIcon);
    }, [frame, frameIndex, page.pageNumber, flyersBranch.data?.iconContrast]);


    useEffect(() => {
        if (timeoutIdRef.current) {
            clearTimeout(timeoutIdRef.current);
        }
        timeoutIdRef.current = setTimeout(() => {
            setIsShowFrame(true);
        }, 250);
        return () => {
            setIsShowFrame(false);
        }
    }, [currentSlideNumber]);

    const openUrlFromFrame = (frame: IFlyerRuntimePageSpot) => {
        if (frame.frameLink) {
            openUrlInAnotherTab(frame.frameLink, "", `frameUrl${frame.frameLink}`)
        }
    }

    const onClickOnFrame = (frame: IFlyerRuntimePageSpot, frameIndex: number, mouseEvent: React.MouseEvent<HTMLDivElement>) => {
        switch (frame.frameType) {
            case 1:
                dispatch(addSelectedSpot(frame, frameIndex, mouseEvent, page?.displayImageUrl, true));
                setTimeout(() => {
                    dispatch(changeLoadingStatusForSelectedFrame(false));
                }, 500)
                if (!isMobile) {
                    if (isHelpScreen) {
                        dispatch(toggleHelpScreen())
                    }
                    if (isTocOpen) {
                        dispatch(toggleToc())
                    }
                    if (showSearch) {
                        dispatch(toggleSearch())
                    }
                    if (shareMenu.isOpen) {
                        dispatch(toggleShareMenu())
                    }
                }
                break;
            case 2:
                const jumpPage = frame.frameLink && Number(frame.frameLink) > 0 ? Number(frame.frameLink) : 1;
                const pageStringToNumberIndex = jumpPage - 1;
                if (flyersBranch.data?.pages) {
                    const maxPagesIndex = flyersBranch.data?.pages.length - 1;
                    const lastPageIndex = calculateTotalPagesForFlyer(maxPagesIndex, flyersBranch.data?.pages.length);
                    if (isVerticalScrolling && isOnePageOrientation) {
                        let legalPageIndex = pageStringToNumberIndex > 0 && pageStringToNumberIndex <= flyersBranch.data?.pages.length ? pageStringToNumberIndex : 0;
                        dispatch(changeCarouselActiveSlide(legalPageIndex))
                        const contentContainer = document.getElementById(`mainSliderWrapper`);
                        if (contentContainer) {
                            scrollToPageForFlyer(contentContainer, legalPageIndex, true);
                        }
                    } else if (isVerticalScrolling && !isOnePageOrientation) {
                        let legalPageIndex = pageStringToNumberIndex > 0 && pageStringToNumberIndex <= maxPagesIndex ? Math.round(pageStringToNumberIndex / 2) : 0;
                        dispatch(changeCarouselActiveSlide(legalPageIndex));
                        const contentContainer = document.getElementById(`mainSliderWrapper`);
                        if (contentContainer) {
                            scrollToPageForFlyer(contentContainer, legalPageIndex, true);
                        }
                    } else {
                        if (sliderRef) {
                            if (isHebrewStyle && !isMobile) {
                                if (!isOnePageOrientation) {
                                    // (sliderRef.current as any).slickGoTo(goToNumber);
                                    if (pageStringToNumberIndex > 0 && pageStringToNumberIndex <= maxPagesIndex) {
                                        (sliderRef.current as any).slickGoTo(lastPageIndex - (Math.round(pageStringToNumberIndex / 2)));
                                        // dispatch(changeCarouselActiveSlide(lastPageIndex - (Math.round(pageStringToNumberIndex / 2))));
                                    } else {
                                        (sliderRef.current as any).slickGoTo(lastPageIndex);
                                        // dispatch(changeCarouselActiveSlide(lastPageIndex));
                                    }
                                } else {
                                    if (pageStringToNumberIndex > 0 && pageStringToNumberIndex <= flyersBranch.data?.pages.length) {
                                        (sliderRef.current as any).slickGoTo(flyersBranch.data?.pages ? flyersBranch.data?.pages.length - 1 - pageStringToNumberIndex : 0);
                                        // dispatch(changeCarouselActiveSlide(flyersBranch.data?.pages ? flyersBranch.data?.pages.length - 1 - pageStringToNumberIndex : 0))
                                    } else {
                                        (sliderRef.current as any).slickGoTo(flyersBranch.data?.pages ? flyersBranch.data?.pages.length - 1 : 0);
                                        // dispatch(changeCarouselActiveSlide(flyersBranch.data?.pages ? flyersBranch.data?.pages.length - 1 : 0))
                                    }
                                }
                            } else {
                                if (!isOnePageOrientation) {
                                    // (sliderRef.current as any).slickGoTo(goToNumber);
                                    if (pageStringToNumberIndex > 0 && pageStringToNumberIndex <= maxPagesIndex) {
                                        (sliderRef.current as any).slickGoTo(Math.round(pageStringToNumberIndex / 2));
                                        // dispatch(changeCarouselActiveSlide(Math.round(pageStringToNumberIndex / 2)));
                                    } else {
                                        (sliderRef.current as any).slickGoTo(0);
                                        // dispatch(changeCarouselActiveSlide(0));
                                    }
                                } else {
                                    if (pageStringToNumberIndex > 0 && pageStringToNumberIndex <= flyersBranch.data?.pages.length) {
                                        (sliderRef.current as any).slickGoTo(pageStringToNumberIndex);
                                        // dispatch(changeCarouselActiveSlide(pageStringToNumberIndex))
                                    } else {
                                        (sliderRef.current as any).slickGoTo(0);
                                        // dispatch(changeCarouselActiveSlide(0))
                                    }
                                }

                            }
                        }
                    }
                    if (isMobile) {
                        resetMobileZoom();
                        //scroll to
                        const contentContainer = contentForMobile.current as any;
                        if (contentContainer) {
                            scrollToPageForFlyer(contentContainer, pageStringToNumberIndex);
                        }
                    }
                }
                break;
            case 3:
                openUrlFromFrame(frame);
                // if (isMobile) {
                //     resetMobileZoom();
                // }
                break;
            default:
                if (isExpanded && Boolean(spot) && !isMobile) {
                    dispatch(removeSelectedSpot());
                }
                dispatch(toggleFrameClickPopup(true, frame.frameLink, frame.frameType));
        }
    }

    return (
        <>
            <Box
                key={frame.spotId}
                className={`${classes.spot} ${isFramePromoExistInMyList ? `promoAlreadyInBasket` : ``} ${isMobile ? '' : 'isNotForMobile'} `}
                {...allowedProps}

                sx={{
                    height: frameCoordinates.spotHeight,
                    width: frameCoordinates.spotWidth,
                    top: frameCoordinates.top,
                    left: frameCoordinates.left,
                    border: frame.layout && frame.promo && frame.layout.borderWeightPx && frame.layout.borderColorRgba ? `${frame.layout.borderWeightPx}px solid transparent` : "2px solid transparent",
                    // backgroundColor: frame.layout && frame.promo && frame.layout.fillColorRgba ? frame.layout.fillColorRgba : "transparent"
                }}
            >
                {isFramePromoExistInMyList &&
                    <Box className={classes.checkmarkIconBlock}>
                        <CheckMarkIcon style={{margin: `8px`}}/>
                        <Typography fontSize={isSmallFrameWidth ? "10px" : `14px`} color={`#ffffff`} px={`5px`} sx={{display: `none`}}
                                    fontFamily={flyersBranch.data?.font.toLowerCase()}>
                            {pickedMultiLangCaption}
                        </Typography>
                    </Box>
                }
                {/*{*/}
                {/*    showFrameIcon() ? */}
                <Box
                    onClick={(e) => {
                        if (isMobile) {
                            onClickOnFrame(frame, frameIndex, e);
                        }

                    }}
                    onClickCapture={(e) => {
                        if (!isMobile) {
                            console.log('here?');
                            e.stopPropagation();
                            onClickOnFrame(frame, frameIndex, e);
                            // onClick(() => {
                            //     onClickOnFrame(frame, frameIndex, e);
                            // });
                        }
                    }}
                    className={`${classes.staticSpotElem} 
                    ${isFramePromoExistInMyList ? `promoAlreadyInBasket` : ``} 
                    staticSpotElem 
                    ${isMobile ? '' : 'isNotForMobile'} 
                    ${isShowFrame ? "" : "blockHoverEffect"}
                    ${isFrameContainedSearchWord ? classes.spotHasSearchValue : ''}`}
                >
                    <Box className={`${(isMobile || isVerticalScrolling) ? "" : "frameIconWrapper"} ${isShowFrame ? "showFrameIcon" : ""}`}
                         sx={{
                             top: `calc(${frame.iconYPct}% - 16px)`,
                             left: `calc(${frame.iconXPct}% - 16px)`,
                             // top: isMobile ? `calc(${frame.iconYPct}% - 12px)` : `calc(${frame.iconYPct}% - 16px)`,
                             // left: isMobile ? `calc(${frame.iconXPct}% - 12px)` : `calc(${frame.iconXPct}% - 16px)`,
                             display: `flex`,
                             // padding: isMobile ? `4px` : `6px`,
                             position: `absolute`,
                             // backgroundColor: `white`,
                             borderRadius: `50%`,
                             width: `32px`,
                             height: `32px`,
                             transition: isShowFrame ? `all 250ms ease-in`: "none",
                             "& > svg > rect": {
                                 fill: (iconStyle === 2 || iconStyle === 3) ? mainColor : undefined
                             }
                             // border: `1px solid #000000`,
                         }}
                    >
                        {
                            isShowTooltip ?
                                <Tooltip
                                    id={frame.spotId.toString()}
                                    title={<CustomTooltipContent
                                        frameTitle={frameTooltip}/>}
                                    classes={{
                                        tooltip: classes.customTooltip,
                                    }}
                                    TransitionComponent={Zoom}
									enterDelay={1000}
                                    // title={frame.tooltip ? frame.tooltip : ``}
                                    disableInteractive
                                    arrow={true}
                                    placement={`top`}
                                >
                                    {icon}
                                </Tooltip>
                                :
                                <>
                                    {icon}
                                </>
                        }
                    </Box>
                </Box>
            </Box>

            {
                frame.layout && frame.promo && frameCoordinates &&
                <LayoutForFrame layoutData={frame.layout} page={page} promo={frame.promo}
                                frameCoordinates={frameCoordinates} framesWrapperRef={framesWrapperRef}/>
            }

        </>
    );
}